<svelte:options customElement={{ tag: "bt-interests-framework", shadow: "none" }} />

<script lang="ts">
  import { onMount } from 'svelte';
  import { User, Users, Globe } from 'lucide-svelte';
  import ActionButton from '$shared/action-button/ActionButton.svelte';

  export let roleHeadingText = '';
  export let skillsHeadingText = '';
  export let skillsBlurbText = '';
  export let individualContributorText = '';
  export let teamManagerText = '';
  export let seniorLeaderText = '';
  export let leadYourselfText = '';
  export let leadOthersText = '';
  export let leadTheBusinessText = '';
  export let individualContributorBlurbText = '';
  export let teamManagerBlurbText = '';
  export let seniorLeaderBlurbText = '';
  export let saveRequirementsText = '';
  export let cancelText = '';
  export let saveText = '';
  export let homePath = '/';

  // Roles and domains both come from the server.
  let possibleTypes: string[] = [];
  let domains: {id: number, name: string, competencies: {id: number, name: string}[]}[] = [];

  let selectedRole: string = '';
  let selectedIds: number[] = [];

  let isLoading = true;
  let isSaving = false;

  // The server enforces this too.
  $: canSave = selectedRole !== '' && selectedIds.length >= 3;

  // Tracking reports on skill names, but the page works in ids.
  const selectedSkillNames = () =>
    domains
      .flatMap((domain) => domain.competencies)
      .filter((competency) => selectedIds.includes(competency.id))
      .map((competency) => competency.name);

  const trackRoleSelected = (role: string) => {
    window.amplitude?.track('Interest Role Selected', { role });
  };

  // Only a selection counts. Unticking a skill is not a funnel step, and counting
  // it would inflate the totals whenever someone changes their mind.
  const trackSkillSelected = (skill: string, isSelected: boolean) => {
    if (!isSelected) return;

    window.amplitude?.track('Interest Skill Selected', { skill });
  };

  const cancel = () => {
    window.location.href = homePath;
  };

  const save = async () => {
    if (!canSave || isSaving) return;
    isSaving = true;

    const response = await fetch('/interests', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content')
      },
      body: JSON.stringify({learner_type: selectedRole, competency_ids: selectedIds})
    });

    if (!response.ok) {
      // Only reachable if the page and the server disagree
      isSaving = false;
      return;
    }

    window.amplitude?.track('Interest Saved', {
      role: selectedRole,
      skillCount: selectedIds.length,
      skills: selectedSkillNames()
    });

    window.location.href = homePath;
  };

  // The icon and supporting copy for each role. role value is a fixed list on the server
  $: roleDetails = {
    "Individual Contributor": {icon: User, label: leadYourselfText, name: individualContributorText, blurb: individualContributorBlurbText},
    "Team Manager": {icon: Users, label: leadOthersText, name: teamManagerText, blurb: teamManagerBlurbText},
    "Senior Leader": {icon: Globe, label: leadTheBusinessText, name: seniorLeaderText, blurb: seniorLeaderBlurbText}
  };

  const loadInterests = async () => {
    const response = await fetch('/interests.json', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json'}
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    return response.json();
  };

  onMount(async () => {
    const data = await loadInterests();

    possibleTypes = data.possible_types;
    domains = data.domains;

    // Seeded from what the user actually saved, so returning to the page shows
    // their current picks rather than a blank slate.
    selectedRole = data.learner_type || '';
    selectedIds = data.selected_competency_ids || [];

    isLoading = false;
  });
</script>

{#if !isLoading}
  <section class="interests-panel">
    <h2 class="interests-panel__heading">{roleHeadingText}</h2>

    <div class="role-cards">
      {#each possibleTypes as role}
        <label class="role-card" class:role-card--selected={selectedRole === role}>
          <input
            class="role-card__input"
            type="radio"
            name="learner-role"
            value={role}
            bind:group={selectedRole}
            on:change={() => trackRoleSelected(role)}
          />
          <svelte:component this={roleDetails[role]?.icon} class="role-card__icon" aria-hidden="true" />
          <span class="role-card__label">{roleDetails[role]?.label}</span>
          <span class="role-card__name">
            <span class="role-card__marker" aria-hidden="true"></span>{roleDetails[role]?.name ?? role}
          </span>
          <span class="role-card__blurb">{@html roleDetails[role]?.blurb ?? ''}</span>
        </label>
      {/each}
    </div>
  </section>

  <section class="interests-panel">
    <div class="interests-panel__header">
      <h2 class="interests-panel__heading">{skillsHeadingText}</h2>
      <p class="interests-panel__blurb">{skillsBlurbText}</p>
    </div>

    <div class="domains">
      {#each domains as domain}
        <div class="domain">
          <h3 class="domain__heading">{domain.name}</h3>
          <div class="domain__options">
            {#each domain.competencies as competency}
              <label class="skill" class:skill--selected={selectedIds.includes(competency.id)}>
                <input
                  class="skill__input"
                  type="checkbox"
                  value={competency.id}
                  bind:group={selectedIds}
                  on:change={(event) =>
                    trackSkillSelected(competency.name, event.currentTarget.checked)}
                />
                <span class="skill__marker" aria-hidden="true"></span>{competency.name}
              </label>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  </section>

  <div class="interests-actions">
    <p class="interests-actions__requirements">{saveRequirementsText}</p>
    <ActionButton design="text-button" on:click={cancel}>{cancelText}</ActionButton>
    <ActionButton design="primary" isDisabled={!canSave} on:click={save}>{saveText}</ActionButton>
  </div>
{/if}

<style>
  :global(bt-interests-framework) {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 24px 0;
  }

  :global(main.pb-20:has(bt-interests-framework)) {
    padding-bottom: 0;
  }

  .interests-panel {
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: white;
    border: 1px solid theme('colors.neutral.200');
    border-radius: 8px;
    padding: 24px;
  }
  .interests-panel__heading {
    font-family: theme('fontFamily.serif');
    font-size: 20px;
    font-weight: 400;
    line-height: 30px;
    letter-spacing: -0.1px;
    color: theme('colors.black');
    margin: 0;
  }
  .interests-panel__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .interests-panel__blurb {
    font-size: 14px;
    line-height: 21px;
    color: theme('colors.neutral.600');
    margin: 0;
  }

  .domains {
    display: grid;
    grid-template-columns: repeat(5, auto);
    justify-content: space-between;
    gap: 24px;
  }
  .domain {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .domain__heading {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 1px;
    line-height: 1;
    text-transform: uppercase;
    color: theme('colors.neutral.600');
    margin: 0;
  }
  .domain__options {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .skill {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: white;
    border: 1px solid theme('colors.neutral.200');
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
    color: theme('colors.black');
    white-space: nowrap;
    cursor: pointer;
  }
  .skill:hover,
  .skill--selected {
    border-color: theme('colors.black');
    box-shadow: inset 0 0 0 1px theme('colors.black');
  }
  .skill:has(.skill__input:focus-visible) {
    outline: 2px solid theme('colors.black');
    outline-offset: 2px;
  }
  .skill__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .skill__marker {
    visibility: hidden;
    width: 8px;
    height: 8px;
    background: theme('colors.yellow');
    flex-shrink: 0;
  }
  .skill--selected .skill__marker {
    visibility: visible;
  }
  .skill::after {
    content: '';
    width: 8px;
    flex-shrink: 0;
  }

  .interests-actions {
    position: sticky;
    bottom: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 24px;
    margin: 0 -24px;
    padding: 16px 24px;
    background: white;
    border-top: 1px solid theme('colors.neutral.200');
  }

  .interests-actions__requirements {
    font-size: 14px;
    line-height: 21px;
    color: theme('colors.neutral.600');
    margin: 0;
  }
  
  :global(.interests-actions .action-button) {
    flex: none;
    min-height: 40px;
    padding: 10px 16px;
  }
  :global(.interests-actions .action-button.text-button) {
    background: transparent;
    color: theme('colors.black');
  }

  :global(.interests-actions .action-button.text-button:hover) {
    background: transparent;
    color: theme('colors.black');
    text-decoration: underline;
  }
  :global(.interests-actions .action-button.primary:not(:disabled)) {
    background: theme('colors.black');
    border-color: theme('colors.black');
    color: white;
  }
  :global(.interests-actions .action-button.primary:disabled) {
    background: theme('colors.neutral.200');
    border-color: theme('colors.neutral.200');
    color: theme('colors.neutral.300');
  }

  @media (max-width: 64rem) {
    .role-card__blurb :global(br) {
      display: none;
    }
    .domains {
      grid-template-columns: repeat(2, auto);
      justify-content: start;
      gap: 32px;
    }
  }

  .role-cards {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    justify-content: center;
    gap: 24px;
    width: 100%;
  }
  .role-card {
    position: relative;
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: white;
    border: 1px solid theme('colors.neutral.200');
    border-radius: 8px;
    padding: 24px;
    cursor: pointer;
  }

  .role-card:hover,
  .role-card--selected {
    border-color: theme('colors.black');
    box-shadow: inset 0 0 0 1px theme('colors.black');
  }
  .role-card:has(.role-card__input:focus-visible) {
    outline: 2px solid theme('colors.black');
    outline-offset: 2px;
  }

  .role-card__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .role-card__label {
    font-size: 12px;
    letter-spacing: 1px;
    line-height: 1;
    text-transform: uppercase;
    color: theme('colors.neutral.600');
  }
  .role-card__name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
    color: theme('colors.black');
  }
  .role-card__marker {
    display: none;
    width: 8px;
    height: 8px;
    background: theme('colors.yellow');
    flex-shrink: 0;
  }
  .role-card--selected .role-card__marker {
    display: block;
  }
  .role-card__blurb {
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    color: theme('colors.neutral.600');
  }
</style>
